import React from 'react';
import { Link } from 'react-router-dom';
import {
  Star,
  ThumbsUp,
  Users,
  Award,
} from 'lucide-react';
import { SEO } from '../../../shared/components';

// --- Data Structures ---
interface Review {
    name: string;
    role: string;
    rating: number;
    quote: string;
    isGoogleReview?: boolean;
}

interface VideoTestimonial {
    title: string;
    description: string;
}

// Real Google Reviews from Tuition Point Qatar
const parentReviews: Review[] = [
    {
        name: "Deepa Jerold",
        role: "Google Review",
        rating: 5,
        quote: "This Centre provides a wonderful learning environment for students. The teachers are patient, well-qualified, and take special care to ensure that every student understands the concepts thoroughly. Regular tests and personal attention help children build confidence and improve their performance. The centre also maintains good communication with parents about progress. I'm very happy with the results and strongly recommend Tuition Point Centre to anyone looking for quality education and guidance.",
        isGoogleReview: true,
    },
    {
        name: "Astha Sharma",
        role: "Google Review",
        rating: 5,
        quote: "One month before my daughter's exams we enrolled her at Tuition Point. The teacher's here are really nice. They give many worksheets, examples and explain the concept properly to the kids. Am very satisfied with the teachers and my daughter scored really well in her exams. Highly recommend!",
        isGoogleReview: true,
    },
    {
        name: "Ponnu Monu",
        role: "Google Review",
        rating: 5,
        quote: "My daughter is studying in this institution from past 3 months and has really improved her academic skills. Educators create an environment where questions are welcomed, fostering a sense of confidence in the students. The personalized attention and approachability of the teachers have played a pivotal role in my child's academic progress. I highly recommend Tuition Point Education Center for their commitment to excellence and the nurturing rapport they establish with their students.",
        isGoogleReview: true,
    },
    {
        name: "Niyas Kassim",
        role: "Google Review",
        rating: 5,
        quote: "Tuition Point has exceeded my expectations in every way. The staff is both professional and approachable, creating a welcoming environment for learning. The personalized attention and comprehensive resources have been invaluable in boosting my daughter's confidence and academic performance. A top choice for anyone looking to excel!",
        isGoogleReview: true,
    },
    {
        name: "Aaleen",
        role: "Google Review",
        rating: 5,
        quote: "Tuition Point is excellent! The teachers are knowledgeable, patient, and supportive. My grades have improved significantly, and I now feel more confident in college. Highly recommended for quality academic support!",
        isGoogleReview: true,
    },
    {
        name: "Shiva J",
        role: "Google Review",
        rating: 5,
        quote: "I admitted 2 of my kids, Year 8 and Year 2 to this center 2 months ago and I am satisfied with the way the tuition point is coaching the kids on the key subjects. Also the homework given is engaging the kids at home also. The teachers are experienced in good way of teaching, patient and kind enough to engage the kids in the classes. Kids are also very much interested to go to the classes without wanting to have a break. As a parent feel happy if the kids are enjoying the classes. Thanks to the center.",
        isGoogleReview: true,
    },
    {
        name: "Amiro",
        role: "Google Review",
        rating: 5,
        quote: "I had an excellent experience with Tuition Point, especially with the Chemistry teacher. After not studying Chemistry for over three years, I was worried about catching up in a short span of time. However, my tutor was amazing! She quickly identified my weak points and tailored a study plan that fit perfectly with my needs and circumstances. Her teaching style was clear and focused, and thanks to her guidance, I was able to achieve an A!",
        isGoogleReview: true,
    },
    {
        name: "Djaml Bouzidi",
        role: "Google Review",
        rating: 5,
        quote: "I would like to thank the Chemistry teacher for the efforts provided for students in grade 10. Big change has happened with nice results at school for my children. Wish all the best for all students with a good continuation for the teacher. Thanks a lot for your efforts to make the study life easy for our children.",
        isGoogleReview: true,
    },
    {
        name: "Saranya Jeyaprakash",
        role: "Google Review",
        rating: 5,
        quote: "I am extremely happy with the progress my child has made at Tuition Point Center. The teachers are very patient, and truly dedicated to helping each student succeed. The personalized attention given to each student ensures they get the support they need, and I have noticed a significant improvement in my child's academic performance. The center also maintains great communication with parents, keeping us updated on our child's progress.",
        isGoogleReview: true,
    },
];

const studentReview: Review = {
    name: "Adil Rahman",
    role: "Grade 11 CBSE Student",
    rating: 5,
    quote: "I was struggling with physics and chemistry before joining Tuition Point in Al Wakra. After their expert coaching, these subjects became my strongest areas. Highly recommend for IIT-JEE preparation.",
};

const videoTestimonials: VideoTestimonial[] = [
    {
        title: "Aisha's Journey - Math Excellence in Al Wakra",
        description: "Watch how Aisha improved her CBSE mathematics scores from 60% to 95% with personalized tutoring at Tuition Point Qatar.",
    },
    {
        title: "Parent Perspective - Success Stories",
        description: "Parents share how Tuition Point Al Wakra transformed their children's learning attitude and academic performance in CBSE and IGCSE.",
    },
    {
        title: "Science Success - Board Exam Preparation",
        description: "Students discuss how Tuition Point's expert science tutoring prepared them for CBSE board exams and entrance tests.",
    },
];

// --- Reusable Components ---
const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
    <div className="flex text-accent">
        {[...Array(5)].map((_, i) => (
            <Star key={i} className={`w-4 h-4 ${i < rating ? 'fill-current' : ''}`} />
        ))}
    </div>
);

const GoogleIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
);

const ReviewCard: React.FC<{ review: Review, delay: number }> = ({ review, delay }) => (
    <div className={`bg-white rounded-xl shadow-lg text-foreground p-6 animate-slide-up animation-delay-${delay}`}>
        <div className="flex items-start gap-4 mb-4">
            <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-600">{review.name.charAt(0)}</span>
            </div>
            <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-display font-bold text-lg">{review.name}</h3>
                    {review.isGoogleReview && (
                        <GoogleIcon className="w-5 h-5" />
                    )}
                </div>
                <p className="text-sm text-muted-foreground mb-2">{review.role}</p>
                <StarRating rating={review.rating} />
            </div>
        </div>
        <p className="text-muted-foreground italic">"{review.quote}"</p>
    </div>
);

const TuitionPointReviews: React.FC = () => {
    const averageRating = 4.9;
    const totalReviews = 42;

    return (
        <div className="bg-accent text-white min-h-screen">
            <SEO
                title="Reviews & Testimonials - 4.9★ Rating | Tuition Point Al Wakra Qatar"
                description="Read 42+ parent and student reviews of Tuition Point Al Wakra. 4.9★ rating for CBSE, IGCSE, IB tutoring in Qatar. Success stories from BPS and DPS admissions."
                keywords="tuition point reviews Qatar, parent testimonials Al Wakra, student feedback Qatar, CBSE tuition reviews, IGCSE tutoring ratings, tuition center testimonials Qatar, education reviews Al Wakra, best tutoring Qatar"
            />

            {/* --------------------- Page Header --------------------- */}
            <section className="pt-16 pb-12 md:pt-20 md:pb-16 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 animate-slide-up">
                            Reviews & Testimonials
                        </h1>
                        <p className="text-lg text-white/80 animate-slide-up">
                            Hear from parents and students about their experience with CBSE, IGCSE, and IB tutoring at Tuition Point.
                        </p>
                    </div>
                </div>
            </section>

            {/* --------------------- Overall Rating Stats --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-white rounded-xl shadow-lg text-foreground animate-fade-in shadow-xl p-8 md:p-12">
                            <div className="text-center">
                                <div className="flex items-center justify-center gap-3 mb-4">
                                    <span className="text-6xl font-display font-bold text-accent">{averageRating}</span>
                                    <div>
                                        <div className="flex text-accent mb-2">
                                            {[...Array(5)].map((_, i) => (
                                                <Star key={i} className="w-6 h-6 fill-current" />
                                            ))}
                                        </div>
                                        <p className="text-muted-foreground">Based on {totalReviews}+ reviews</p>
                                    </div>
                                </div>
                                <p className="text-lg text-muted-foreground">
                                    Trusted by families across Al Wakra and Qatar for quality K-12 education
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Key Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 max-w-5xl mx-auto">
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center mx-auto mb-4">
                                <ThumbsUp className="w-8 h-8 text-accent" />
                            </div>
                            <h3 className="text-3xl font-display font-bold mb-2">98%</h3>
                            <p className="text-muted-foreground">Parent Satisfaction Rate</p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center mx-auto mb-4">
                                <Users className="w-8 h-8 text-accent" />
                            </div>
                            <h3 className="text-3xl font-display font-bold mb-2">200+</h3>
                            <p className="text-muted-foreground">Active Students in Qatar</p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center mx-auto mb-4">
                                <Award className="w-8 h-8 text-accent" />
                            </div>
                            <h3 className="text-3xl font-display font-bold mb-2">93%+</h3>
                            <p className="text-muted-foreground">Average Board Exam Scores</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* --------------------- Parent Reviews --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">Parent Reviews</h2>
                        <p className="text-lg text-white/80 animate-slide-up">
                            What parents say about CBSE, IGCSE, and IB tutoring at Tuition Point Education Center
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {parentReviews.map((review, index) => (
                            <ReviewCard key={index} review={review} delay={(index % 2) + 1} />
                        ))}
                    </div>
                </div>
            </section>

            {/* --------------------- Student Review --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">Student Success Story</h2>
                        <p className="text-lg text-white/80 animate-slide-up">
                            Direct feedback from students achieving excellence in Examination
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto">
                        <div className="bg-white rounded-xl shadow-lg text-foreground p-8 animate-fade-in shadow-xl">
                            <div className="flex flex-col md:flex-row items-start gap-6">
                                <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                                    <span className="text-4xl font-bold text-green-600">{studentReview.name.charAt(0)}</span>
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-start justify-between mb-3">
                                        <div>
                                            <h3 className="font-display font-bold text-xl mb-1">{studentReview.name}</h3>
                                            <p className="text-muted-foreground mb-2">{studentReview.role}</p>
                                        </div>
                                        <StarRating rating={studentReview.rating} />
                                    </div>
                                    <p className="text-muted-foreground text-lg italic">"{studentReview.quote}"</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --------------------- Video Testimonials --------------------- */}
            <section className="py-24 bg-accent text-white">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 animate-slide-up">
                            Success Stories from Students
                        </h2>
                        <p className="text-lg text-white text-opacity-90 animate-slide-up">
                            Watch real stories of academic transformation at Tuition Point Education Center
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {videoTestimonials.map((video, index) => (
                            <div key={index} className={`bg-white rounded-xl p-6 animate-slide-up animation-delay-${index + 1}`}>
                                <h3 className="text-xl font-display font-bold mb-3 text-primary">{video.title}</h3>
                                <p className="text-muted-foreground">{video.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --------------------- CTA Section --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6 animate-slide-up">
                            Join Our Academic Journey from Basics to Brilliance
                        </h2>
                        <p className="text-lg text-white/80 mb-8 animate-slide-up">
                            Experience the quality CBSE, IGCSE, and IB tutoring that parents and students trust
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
                            <Link
                                to="/enrollment"
                                className="bg-white text-accent hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg shadow-xl transition duration-300 sm:w-auto text-lg"
                            >
                                Enroll Today
                            </Link>
                            <Link
                                to="/contact"
                                className="border-2 border-white text-white hover:bg-white hover:text-accent font-semibold py-3 px-8 rounded-lg transition duration-300 sm:w-auto text-lg"
                            >
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default TuitionPointReviews;
